// Shared UI primitives for tool pages
import React, { useRef, useState } from "react";
import { Upload, Download, RefreshCw, ImageIcon } from "lucide-react";
import { TOOL } from "@/constants/testIds";

export function UploadZone({ onFile, accept = "image/*", multiple = false, onFiles, hint = "PNG, JPG, WebP up to 20MB" }) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);

  const handle = (files) => {
    if (!files || files.length === 0) return;
    if (multiple && onFiles) onFiles(Array.from(files));
    else if (onFile) onFile(files[0]);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
      className={`border-2 border-dashed rounded-2xl p-10 text-center transition-colors cursor-pointer ${drag ? "border-blue-500 bg-blue-50/40 dark:bg-blue-500/5" : "border-slate-300 dark:border-white/15 hover:border-blue-400 dark:hover:border-blue-500/40"}`}
      onClick={() => ref.current?.click()}
      data-testid="upload-zone"
    >
      <input
        ref={ref}
        type="file"
        accept={accept}
        multiple={multiple}
        data-testid={TOOL.uploadInput}
        onChange={(e) => handle(e.target.files)}
        className="hidden"
      />
      <div className="w-14 h-14 mx-auto rounded-2xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 grid place-items-center">
        <Upload size={22} />
      </div>
      <p className="mt-4 text-base font-medium text-slate-900 dark:text-white">Drop your image here</p>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">or click to browse — {hint}</p>
      <button
        type="button"
        data-testid={TOOL.uploadButton}
        className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
        onClick={(e) => { e.stopPropagation(); ref.current?.click(); }}
      >
        <ImageIcon size={16} /> Choose Image
      </button>
    </div>
  );
}

export function ResultCard({ children }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#121316] p-6">
      {children}
    </div>
  );
}

export function DownloadButton({ href, filename, onClick, children = "Download Result" }) {
  if (href) {
    return (
      <a
        href={href}
        download={filename}
        data-testid={TOOL.downloadButton}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
      >
        <Download size={16} /> {children}
      </a>
    );
  }
  return (
    <button
      onClick={onClick}
      data-testid={TOOL.downloadButton}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
    >
      <Download size={16} /> {children}
    </button>
  );
}

export function ResetButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={TOOL.resetButton}
      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
    >
      <RefreshCw size={14} /> Reset
    </button>
  );
}

export function humanBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export async function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = reject;
    img.src = url;
  });
}
